import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../App';

const MESICE = ['Leden','Únor','Březen','Duben','Květen','Červen','Červenec','Srpen','Září','Říjen','Listopad','Prosinec'];

export default function SchvalovaniTym({ onRefresh }) {
  const { profile: user } = useAuth();
  const now = new Date();
  const [rok, setRok] = useState(now.getFullYear());
  const [mesic, setMesic] = useState(now.getMonth() + 1);
  const [vykazy, setVykazy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('čeká na schválení');
  const [hledani, setHledani] = useState('');
  const [editVykaz, setEditVykaz] = useState(null);
  const [zakazky, setZakazky] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pullRefreshing, setPullRefreshing] = useState(false);
  const touchStartY = useRef(null);

  useEffect(() => {
    supabase.from('zakazky').select('*').eq('aktivni', true).eq('uzavrena', false).order('nazev')
      .then(({ data }) => setZakazky(data || []));
  }, []);

  useEffect(() => { fetchVykazy(); }, [rok, mesic]);

  const fetchVykazy = useCallback(async () => {
    setLoading(true);
    const od = `${rok}-${String(mesic).padStart(2,'0')}-01`;
    const do_ = `${rok}-${String(mesic).padStart(2,'0')}-${new Date(rok, mesic, 0).getDate()}`;

    let query = supabase
      .from('vykazy')
      .select('*, zakazky(nazev), uzivatele!vykazy_zamestnanec_id_fkey(jmeno)')
      .gte('datum', od)
      .lte('datum', do_)
      .order('datum', { ascending: false });

    if (user.role === 'hr') {
      // HR vidí výkazy úplně všech – žádný filtr
    } else if (user.role === 'vedouci' || user.role === 'jednatel') {
      const { data: vazby } = await supabase
        .from('zamestnanec_vedouci')
        .select('zamestnanec_id')
        .eq('vedouci_id', user.id);

      const idcka = (vazby || []).map(v => v.zamestnanec_id);
      if (idcka.length === 0) { setVykazy([]); setLoading(false); return; }

      const { data: aktivni } = await supabase
        .from('uzivatele')
        .select('id')
        .in('id', idcka)
        .eq('aktivni', true);

      const aktivniIdcka = (aktivni || []).map(u => u.id);
      if (aktivniIdcka.length === 0) { setVykazy([]); setLoading(false); return; }

      query = query.in('zamestnanec_id', aktivniIdcka);
    } else {
      setVykazy([]);
      setLoading(false);
      return;
    }

    const { data } = await query;
    setVykazy(data || []);
    setLoading(false);
  }, [rok, mesic, user]);

  const handleTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const handleTouchEnd = async (e) => {
    if (touchStartY.current === null) return;
    const diff = e.changedTouches[0].clientY - touchStartY.current;
    if (diff > 80 && window.scrollY === 0) {
      setPullRefreshing(true);
      await fetchVykazy();
      setPullRefreshing(false);
    }
    touchStartY.current = null;
  };

  const akce = async (id, stav) => {
    await supabase.from('vykazy').update({ stav }).eq('id', id);
    await fetchVykazy();
    onRefresh();
  };

  const smazatVykaz = async (id) => {
    if (!window.confirm('Opravdu chcete tento výkaz smazat?')) return;
    await supabase.from('vykazy').delete().eq('id', id);
    await fetchVykazy();
    onRefresh();
  };

  const ulozitEdit = async () => {
    if (!editVykaz) return;
    setSaving(true);
    await supabase.from('vykazy').update({
      hodiny: parseFloat(editVykaz.hodiny),
      zakazka_id: editVykaz.zakazka_id,
      datum: editVykaz.datum,
      poznamka: editVykaz.poznamka,
      stav: editVykaz.stav,
    }).eq('id', editVykaz.id);
    setEditVykaz(null);
    setSaving(false);
    await fetchVykazy();
    onRefresh();
  };

  // Filtrování podle stavu + hledání podle jména, data (i CZ formátu) nebo zakázky
  const filtered = vykazy
    .filter(v => filter === 'vse' ? true : v.stav === filter)
    .filter(v => {
      if (!hledani.trim()) return true;
      const h = hledani.toLowerCase();
      // Převedení data do českého formátu pro snazší vyhledávání
      const datumCZ = v.datum ? v.datum.split('-').reverse().join('.') : '';
      return (
        v.uzivatele?.jmeno?.toLowerCase().includes(h) ||
        datumCZ.includes(h) ||
        v.datum?.includes(h) ||
        v.zakazky?.nazev?.toLowerCase().includes(h)
      );
    });

  const cekajicich = vykazy.filter(v => v.stav === 'čeká na schválení').length;
  const inputStyle = { border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', fontSize: 15, fontFamily: 'DM Sans, sans-serif', outline: 'none', width: '100%', boxSizing: 'border-box', background: '#fff' };

  return (
    <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      {pullRefreshing && (
        <div style={{ textAlign: 'center', padding: '10px', color: '#2d6a4f', fontWeight: 600, fontSize: 14 }}>
          🔄 Aktualizuji…
        </div>
      )}

      {editVykaz && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 480, boxSizing: 'border-box', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', fontFamily: 'DM Sans, sans-serif' }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20, color: '#111' }}>Upravit výkaz</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>Datum</label>
                <input type="date" value={editVykaz.datum} onChange={e => setEditVykaz({...editVykaz, datum: e.target.value})} style={inputStyle} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>Zakázka</label>
                <select value={editVykaz.zakazka_id} onChange={e => setEditVykaz({...editVykaz, zakazka_id: e.target.value})} style={inputStyle}>
                  {zakazky.map(z => <option key={z.id} value={z.id}>{z.nazev}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>Hodiny</label>
                <input type="number" min="0.5" max="24" step="0.5" value={editVykaz.hodiny} onChange={e => setEditVykaz({...editVykaz, hodiny: e.target.value})} style={inputStyle} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>Poznámka</label>
                <textarea value={editVykaz.poznamka || ''} onChange={e => setEditVykaz({...editVykaz, poznamka: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'none' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>Stav</label>
                <select value={editVykaz.stav} onChange={e => setEditVykaz({...editVykaz, stav: e.target.value})} style={inputStyle}>
                  <option value="čeká na schválení">Čeká na schválení</option>
                  <option value="schváleno">Schváleno</option>
                  <option value="zamítnuto">Zamítnuto</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
              <button onClick={() => setEditVykaz(null)} style={{ flex: 1, background: '#f1f5f9', border: 'none', borderRadius: 10, padding: 13, fontSize: 14, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, color: '#555' }}>Zrušit</button>
              <button onClick={ulozitEdit} disabled={saving} style={{ flex: 2, background: '#2d6a4f', color: '#fff', border: 'none', borderRadius: 10, padding: 13, fontSize: 14, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: 700 }}>
                {saving ? 'Ukládám…' : 'Uložit změny'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111', margin: 0 }}>Schválení týmu</h2>
          {cekajicich > 0 && (
            <span style={{ background: '#fef3c7', color: '#92400e', padding: '4px 10px', borderRadius: 20, fontSize: 13, fontWeight: 700 }}>
              {cekajicich} čeká
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <select value={mesic} onChange={e => setMesic(+e.target.value)}
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', fontSize: 14, fontFamily: 'DM Sans, sans-serif', background: '#fff', outline: 'none' }}>
            {MESICE.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
          </select>
          <select value={rok} onChange={e => setRok(+e.target.value)}
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', fontSize: 14, fontFamily: 'DM Sans, sans-serif', background: '#fff', outline: 'none' }}>
            {[2024,2025,2026].map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <button onClick={fetchVykazy}
            style={{ background: '#f0f0f0', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 14, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}>
            🔄 Obnovit
          </button>
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {[['čeká na schválení','⏳ Čeká'],['schváleno','✓ Schválené'],['zamítnuto','✗ Zamítnuté'],['vse','Vše']].map(([val, lbl]) => (
            <button key={val} onClick={() => setFilter(val)} style={{
              background: filter === val ? '#2d6a4f' : '#fff',
              color: filter === val ? '#fff' : '#555',
              border: '1.5px solid ' + (filter === val ? '#2d6a4f' : '#e2e8f0'),
              borderRadius: 20, padding: '7px 16px', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
            }}>{lbl}</button>
          ))}
        </div>

        {/* Vyhledávací pole */}
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: '#aaa' }}>🔍</span>
          <input
            type="text"
            value={hledani}
            onChange={e => setHledani(e.target.value)}
            placeholder="Hledat podle jména, data nebo zakázky…"
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 14px 10px 36px', fontSize: 14, fontFamily: 'DM Sans, sans-serif', outline: 'none', width: '100%', boxSizing: 'border-box', background: '#fff' }}
          />
          {hledani && (
            <button onClick={() => setHledani('')} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#aaa' }}>✕</button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ background: '#fff', borderRadius: 14, padding: 40, textAlign: 'center', color: '#888' }}>Načítám…</div>
      ) : filtered.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 14, padding: 40, textAlign: 'center', color: '#888' }}>
          {hledani ? `Žádné výkazy pro „${hledani}"` : filter === 'čeká na schválení' ? '✓ Vše schváleno!' : 'Žádné výkazy'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(v => (
            <div key={v.id} style={{
              background: '#fff', borderRadius: 14, padding: '18px 20px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
              borderLeft: v.stav === 'schváleno' ? '4px solid #2d6a4f' : v.stav === 'zamítnuto' ? '4px solid #c0392b' : '4px solid #f59e0b',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#111' }}>{v.uzivatele?.jmeno}</div>
                <div style={{ fontWeight: 800, fontSize: 20, color: '#2d6a4f' }}>{v.hodiny} h</div>
              </div>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 6 }}>📅 {v.datum}</div>
              <div style={{ fontSize: 14, color: '#333', marginBottom: v.poznamka ? 4 : 0, fontWeight: 500 }}>📋 {v.zakazky?.nazev}</div>
              {v.poznamka && <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>💬 {v.poznamka}</div>}

              {v.stav === 'čeká na schválení' ? (
                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button onClick={() => akce(v.id, 'schváleno')} style={{ flex: 1, background: '#2d6a4f', color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>✓ Schválit</button>
                  <button onClick={() => akce(v.id, 'zamítnuto')} style={{ flex: 1, background: '#fde8e6', color: '#c0392b', border: 'none', borderRadius: 10, padding: '12px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>✗ Zamítnout</button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
                  <span style={{ display: 'inline-block', padding: '5px 14px', borderRadius: 20, fontSize: 13, fontWeight: 700, background: v.stav === 'schváleno' ? '#d8f3dc' : '#fde8e6', color: v.stav === 'schváleno' ? '#1b4332' : '#c0392b' }}>
                    {v.stav === 'schváleno' ? '✓ Schváleno' : '✗ Zamítnuto'}
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {(user.role === 'hr' || user.role === 'jednatel') && (
                      <button onClick={() => setEditVykaz({ ...v, zakazka_id: v.zakazka_id })}
                        style={{ background: '#f0f0f0', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', color: '#333' }}>
                        ✏️ Upravit
                      </button>
                    )}
                    {user.role === 'hr' && (
                      <button onClick={() => smazatVykaz(v.id)}
                        style={{ background: '#fde8e6', color: '#c0392b', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                        🗑️ Smazat
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
